import { Check } from 'lucide-react';
import LogoutButton from '../../Components/UI/LogoutButton';
import signupLogo from '../../assets/claraview-v2/signup-logo.webp';

/*
 * Shared shell for the three billing screens (Subscription/pricing, SubscriptionPending,
 * BillingUpdateCard): dark radial background, desktop-only 400px plan panel, and the
 * centred content column on the right. Replaces three near-identical copies that carried
 * their own cv2-pricing-* / cv2-pending-* / cv2-billing-* style blocks.
 *
 * The page background is Figma's literal radial-gradient (#192a20/#101b15/#080c09) rather than
 * a cv2 token: cv2-foreground/cv2-background are pure grayscale so they can only mix to gray,
 * and the claraview-tertiary/quaternary olives are a visibly warmer hue than this teal-green
 * glow. Every other tinted value is still derived from the cv2 tokens via color-mix(), since
 * Tailwind's opacity modifier emits nothing for bare `var(--cv2-*)` colors.
 */

const FEATURES = [
    'Unlimited AI Simulations',
    'Patient Dashboard',
    'In-App Messaging',
    'Email Templates',
    'Custom Simulation Link',
    'Coaching & Mentorship',
];

const SHARED_STYLES = `
  .cv2-bill-bg {
    background: radial-gradient(ellipse at 20% 5%, #192a20 0%, #101b15 27.5%, #080c09 55%);
  }
  .cv2-bill-left-panel {
    background-color: rgba(0, 0, 0, 0.25);
    border-right-color: color-mix(in srgb, var(--cv2-foreground) 5%, transparent);
  }
  .cv2-bill-eyebrow { color: color-mix(in srgb, var(--cv2-foreground) 30%, transparent); }
  .cv2-bill-plan-name { color: color-mix(in srgb, var(--cv2-foreground) 75%, transparent); }
  .cv2-bill-price-symbol { color: color-mix(in srgb, var(--cv2-foreground) 35%, transparent); }
  .cv2-bill-price-suffix { color: color-mix(in srgb, var(--cv2-foreground) 32%, transparent); }
  .cv2-bill-plan-copy { color: color-mix(in srgb, var(--cv2-foreground) 28%, transparent); }
  .cv2-bill-feature-text { color: color-mix(in srgb, var(--cv2-foreground) 42%, transparent); }
  .cv2-bill-divider-thin { border-color: color-mix(in srgb, var(--cv2-foreground) 5%, transparent); }
  .cv2-bill-divider-med { border-color: color-mix(in srgb, var(--cv2-foreground) 6%, transparent); }
  .cv2-bill-subcopy { color: color-mix(in srgb, var(--cv2-foreground) 45%, transparent); }
  .cv2-bill-reassurance { color: color-mix(in srgb, var(--cv2-foreground) 20%, transparent); }
  .cv2-bill-error-banner {
    color: var(--cv2-destructive);
    background-color: color-mix(in srgb, var(--cv2-destructive) 10%, transparent);
    border-color: color-mix(in srgb, var(--cv2-destructive) 30%, transparent);
  }
`;

const BillingLayout = ({
    // Only correct for screens a new/re-subscribing clinic reaches — checkout charges whatever
    // price ID the backend picks. Existing subscribers can be grandfathered at an older price we
    // can't know client-side, so /billing/update-card passes price={null} and shows none.
    price = '499',
    priceLabel,
    priceCopy,
    footerLabel,
    footerValue,
    footerTone = 'accent',
    centered = false,
    children,
    actions,
}) => {
    const footerColor = footerTone === 'destructive' ? 'text-cv2-destructive' : 'text-claraview-secondary';

    return (
        <div className="cv2-dark cv2-bill-bg relative min-h-dvh overflow-x-hidden flex">
            <style>{SHARED_STYLES}</style>

            <div className="fixed top-6 right-6 z-20">
                <LogoutButton />
            </div>

            {/* Plan panel — desktop only */}
            <div className="cv2-bill-left-panel hidden lg:flex flex-col w-[400px] shrink-0 border-r px-[52px] py-[52px]">
                <img src={signupLogo} alt="Claraview" className="h-[18px] w-auto object-left object-contain opacity-75 mb-16" />

                <div className="mb-10">
                    <p className="cv2-bill-eyebrow font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-2">Plan</p>
                    <p className="cv2-bill-plan-name font-aeonik text-[22px] leading-7 tracking-[-0.3px] normal-case">Claraview Unlimited</p>
                </div>

                {price && (
                    <div className="mb-12">
                        <p className="cv2-bill-eyebrow font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-1.5">{priceLabel}</p>
                        <div className="flex items-start gap-[3px]">
                            <span className="cv2-bill-price-symbol font-aeonik text-[15px] mt-2.5 normal-case">$</span>
                            <span className="font-aeonik text-[80px] leading-none tracking-[-3px] text-cv2-foreground normal-case">{price}</span>
                            <span className="cv2-bill-price-suffix font-aeonik text-[13px] self-end mb-2.5 normal-case">/mo</span>
                        </div>
                        <p className="cv2-bill-plan-copy font-aeonik text-[13px] mt-1 normal-case">{priceCopy}</p>
                    </div>
                )}

                <div className="cv2-bill-divider-med flex flex-col border-t mb-auto">
                    {FEATURES.map((feature) => (
                        <div key={feature} className="cv2-bill-divider-thin flex items-center justify-between py-3 border-b">
                            <span className="cv2-bill-feature-text font-aeonik text-[13px] normal-case">{feature}</span>
                            <Check className="w-[11px] h-[11px] text-claraview-secondary shrink-0" strokeWidth={2.5} />
                        </div>
                    ))}
                </div>

                <div className="cv2-bill-divider-med mt-10 flex items-baseline justify-between border-t pt-5">
                    <p className="cv2-bill-eyebrow font-spaceMono text-[10px] tracking-[1px] uppercase">{footerLabel}</p>
                    <p className={`font-aeonik text-[28px] tracking-[-0.5px] ${footerColor} normal-case`}>{footerValue}</p>
                </div>
            </div>

            <div className="flex-1 flex items-start lg:items-center justify-center px-7 sm:px-10 lg:px-16 py-10 lg:py-[52px]">
                <div className={`w-full max-w-[380px]${centered ? ' text-center' : ''}`}>
                    <img
                        src={signupLogo}
                        alt="Claraview"
                        className={`lg:hidden h-[18px] w-auto object-contain opacity-75 mb-12${centered ? ' mx-auto' : ' object-left'}`}
                    />

                    {children}

                    {/*
                     * Mobile price band. The plan panel is hidden below lg, so without this a phone
                     * user never sees what they're charged. It sits between the copy and the CTA so
                     * the price is the last thing read before acting, and it carries the due/status
                     * value as a second column rather than a separate stacked row.
                     */}
                    <div className={`cv2-bill-divider-med lg:hidden my-8 py-5 border-y flex items-start justify-between gap-4${centered ? ' text-left' : ''}`}>
                        {price && (
                            <div>
                                <p className="cv2-bill-eyebrow font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-1.5">{priceLabel}</p>
                                <div className="flex items-start gap-[2px]">
                                    <span className="cv2-bill-price-symbol font-aeonik text-[13px] mt-1.5 normal-case">$</span>
                                    <span className="font-aeonik text-[44px] leading-none tracking-[-1.6px] text-cv2-foreground normal-case">{price}</span>
                                    <span className="cv2-bill-price-suffix font-aeonik text-xs self-end mb-1 normal-case">/mo</span>
                                </div>
                                <p className="cv2-bill-plan-copy font-aeonik text-xs mt-1.5 normal-case">{priceCopy}</p>
                            </div>
                        )}

                        {/* Alone in the band without a price, the status has nothing to sit against — left-align it. */}
                        <div className={`shrink-0 ${price ? 'text-right' : 'text-left'}`}>
                            <p className="cv2-bill-eyebrow font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-1.5">{footerLabel}</p>
                            <p className={`font-aeonik text-[22px] leading-none tracking-[-0.4px] ${footerColor} normal-case`}>{footerValue}</p>
                        </div>
                    </div>

                    {actions}
                </div>
            </div>
        </div>
    );
};

export default BillingLayout;
